import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRealtimeEvent } from "../lib/realtime";
import { Send, MessageCircle, Users, LifeBuoy } from "@/components/icons";

export default function Chat() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef();
  const scrollRef = useRef();

  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") {
      // Admin: list of users who have written to them (doctors / secretaries / pharmacy)
      api.get("/support/conversations").then((r) => {
        const list = (r.data || []).map((c) => ({
          id: c.id,
          name: c.name,
          photo_url: c.photo_url,
          role: c.role,
          email: "",
        }));
        setContacts(list);
        if (list.length && !active) setActive(list[0]);
      });
      return;
    }
    // Doctor / secretary: staff + Soporte pinned at the BOTTOM.
    Promise.all([
      api.get("/users/staff"),
      api.get("/support/admin").catch(() => ({ data: null })),
    ]).then(([staff, admin]) => {
      const list = [];
      list.push(...staff.data.filter((u) => u.id !== admin.data?.id));
      if (admin.data) {
        list.push({ ...admin.data, is_support: true });
      }
      setContacts(list);
      const firstRegular = list.find((c) => !c.is_support);
      if (firstRegular && !active) setActive(firstRegular);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!active) return;
    const fetchMsg = () =>
      api.get(`/chat/${active.id}`).then((r) => setMessages(r.data));
    fetchMsg();
    const t = setInterval(fetchMsg, 15000);
    return () => clearInterval(t);
  }, [active?.id]);

  useRealtimeEvent("chat:new", (m) => {
    if (active && (m.from_user_id === active.id || m.to_user_id === active.id)) {
      api.get(`/chat/${active.id}`).then((r) => setMessages(r.data));
    }
  });
  useRealtimeEvent("chat:sent", (m) => {
    if (active && m.to_user_id === active.id) {
      api.get(`/chat/${active.id}`).then((r) => setMessages(r.data));
    }
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    const { data } = await api.post("/chat", {
      to_user_id: active.id,
      content: text.trim(),
    });
    setMessages((prev) => [...prev, data]);
    setText("");
  };

  const initials = (n) =>
    (n || "?")
      .split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  // Filter out tiny placeholder photo URLs (e.g. broken 8-byte PNG seed values).
  const hasValidPhoto = (url) => !!url && url.length > 100;

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100dvh - 8rem)" }}
      data-testid="chat-page"
    >
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-3xl font-extrabold text-black tracking-tight">Chat</h1>
        <p className="text-sm text-black/60">
          {contacts.length} contacto(s) · Mensajería en tiempo real
        </p>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-4 flex-1 min-h-0">
        {/* Contacts column */}
        <div className="glass rounded-[28px] flex flex-col min-h-0 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E7EB] flex-shrink-0">
            <Users className="w-5 h-5 text-black" />
            <div className="font-bold text-black text-sm">Contactos</div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-2">
            {contacts.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c)}
                data-testid={`chat-contact-${c.id}`}
                className={`w-full text-left p-3 rounded-xl flex items-center gap-3 mb-1 transition ${
                  active?.id === c.id
                    ? "bg-[#01BC8E] text-white"
                    : "hover:bg-black/[0.04] text-black"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex-shrink-0 overflow-hidden relative ${
                    c.is_support
                      ? "bg-[#01BC8E]"
                      : active?.id === c.id
                      ? "bg-white"
                      : "bg-[#DBEAFE] border border-[#E5E7EB]"
                  }`}
                >
                  {c.is_support ? (
                    <div className="absolute inset-0 flex items-center justify-center text-white">
                      <LifeBuoy className="w-5 h-5" />
                    </div>
                  ) : (
                    <>
                      <div
                        className={`absolute inset-0 flex items-center justify-center font-bold text-xs ${
                          active?.id === c.id ? "text-[#01BC8E]" : "text-[#1B405B]"
                        }`}
                      >
                        {initials(c.name)}
                      </div>
                      {hasValidPhoto(c.photo_url) && (
                        <img
                          src={c.photo_url}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      )}
                    </>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-bold truncate">
                    {c.is_support ? "Soporte" : c.name}
                  </div>
                  <div
                    className={`text-xs truncate ${
                      active?.id === c.id ? "text-white/75" : "text-black/55"
                    }`}
                  >
                    {c.is_support
                      ? c.name
                      : c.role === "doctor"
                      ? "Doctor"
                      : c.role === "secretary"
                      ? "Secretaria"
                      : c.role === "pharmacy"
                      ? "Farmacia"
                      : c.role || ""}
                  </div>
                </div>
              </button>
            ))}
            {contacts.length === 0 && (
              <div className="text-sm text-black/55 p-4 text-center">
                Sin contactos disponibles.
              </div>
            )}
          </div>
        </div>

        {/* Conversation column */}
        <div className="glass rounded-[28px] flex flex-col min-h-0 overflow-hidden">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-black/60">
              <MessageCircle className="w-8 h-8 mr-2" /> Selecciona un contacto
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-[#E5E7EB] flex-shrink-0 flex items-center gap-3">
                <div
                  className={`w-11 h-11 rounded-full overflow-hidden flex-shrink-0 relative ${
                    active.is_support ? "bg-[#01BC8E]" : "bg-[#DBEAFE]"
                  }`}
                >
                  {active.is_support ? (
                    <div className="absolute inset-0 flex items-center justify-center text-white">
                      <LifeBuoy className="w-6 h-6" />
                    </div>
                  ) : (
                    <>
                      <div className="absolute inset-0 flex items-center justify-center font-bold text-sm text-[#1B405B]">
                        {initials(active.name)}
                      </div>
                      {hasValidPhoto(active.photo_url) && (
                        <img
                          src={active.photo_url}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      )}
                    </>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-black truncate">
                    {active.is_support ? "Soporte" : active.name}
                  </div>
                  <div className="text-xs text-black/60 truncate">
                    {active.is_support ? active.name : active.email}
                  </div>
                </div>
              </div>
              <div
                ref={scrollRef}
                className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3"
                data-testid="chat-messages"
              >
                {messages.map((m) => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div
                      key={m.id}
                      className={`flex ${mine ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                          mine
                            ? "bg-[#01BC8E] text-white rounded-br-md"
                            : "bg-white text-black border border-[#E5E7EB] rounded-bl-md"
                        }`}
                      >
                        <div className="whitespace-pre-wrap break-words">
                          {m.content}
                        </div>
                        <div
                          className={`text-[10px] mt-1 ${
                            mine ? "text-white/70" : "text-black/55"
                          }`}
                        >
                          {new Date(m.created_at).toLocaleTimeString("es-ES", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form
                onSubmit={send}
                className="px-3 py-3 border-t border-[#E5E7EB] flex gap-2 flex-shrink-0 bg-white"
              >
                <input
                  data-testid="chat-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escribe un mensaje…"
                  className="flex-1 bg-gray-50 rounded-full px-5 py-2.5 border border-[#E5E7EB] outline-none focus:bg-white focus:border-black"
                />
                <button
                  data-testid="chat-send-button"
                  type="submit"
                  className="w-11 h-11 rounded-xl bg-[#01BC8E] text-white flex items-center justify-center hover:bg-[#01BC8E] disabled:opacity-50 flex-shrink-0"
                  disabled={!text.trim()}
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
